import type {
  BillHistoryResponse,
  BillResponse,
  CreateBillRequest,
  CreateGrnRequest,
  CreatePurchaseOrderRequest,
  GoodsReceiptResponse,
  GrnHistoryResponse,
  PurchaseOrderResponse,
  SendPurchaseOrderResponse,
  UpdateBillRequest,
  UpdateGrnRequest,
  UpdatePurchaseOrderRequest,
} from '@palaccount/contracts';
import {
  DEFAULT_PAGE_SIZE,
  MAX_PICKER_IDS,
  PICKER_PAGE_SIZE,
  pickerIds,
} from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { api } from '@/api';
import type { TypeaheadOption } from '@/components/ui/typeahead';
import { useDebounced, usePickerState, type PickerState } from '@/lib/typeahead';
import { throwApiError } from '@/lib/server-messages';

/** PUR-01/PUR-02/PUR-03 data layer — same shape as sales/receipts. */

export const PURCHASE_ORDERS_KEY = ['purchase-orders'] as const;
export const BILLS_KEY = ['purchase-bills'] as const;

export interface OrderListFilters {
  partnerId?: string | undefined;
  status?: PurchaseOrderResponse['status'] | undefined;
  /** Sent or part-received — the orders a GRN may be written against. */
  receivable?: boolean | undefined;
  /** GEN-50/GEN-25: the printed order code or the supplier's name, from the list's search box. */
  q?: string | undefined;
  /** Wave 3B — the list's window on the order date, both inclusive. */
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

function orderQuery(filters: OrderListFilters) {
  return {
    ...(filters.partnerId ? { partnerId: filters.partnerId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.receivable ? { receivable: 'true' as const } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
  };
}

/** PUR-01's register SCREEN — one page at a time (NFR-42). */
export function usePagedPurchaseOrders(
  filters: OrderListFilters,
  page: number,
  pageSize: number,
) {
  return useQuery({
    queryKey: [...PURCHASE_ORDERS_KEY, 'page', filters, page, pageSize],
    queryFn: async () => {
      const response = await api.purchaseOrders.list({
        query: { ...orderQuery(filters), page, pageSize },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/** The export's page read — the same query the screen shows, imperative so `fetchAllPages` can walk it. */
export async function fetchPurchaseOrdersPage(
  filters: OrderListFilters,
  page: number,
  pageSize: number,
): Promise<{ rows: PurchaseOrderResponse[]; total: number }> {
  const response = await api.purchaseOrders.list({ query: { ...orderQuery(filters), page, pageSize } });
  if (response.status !== 200) throwApiError(response.status, response.body);
  return { rows: response.body.orders, total: response.body.total };
}

/** Wave 3B — PUR-01's list tiles over the whole register (PAGINATION.md §3 step 10). */
export function usePurchaseOrderStats() {
  return useQuery({
    queryKey: [...PURCHASE_ORDERS_KEY, 'stats'],
    queryFn: async () => {
      const response = await api.purchaseOrders.stats();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/** GEN-50 — the GRN and import screens' "which order?" picker. */
export function usePurchaseOrderSearch(q: string, filters: OrderListFilters = {}) {
  return useQuery({
    queryKey: [...PURCHASE_ORDERS_KEY, 'search', q, filters],
    queryFn: async (): Promise<PurchaseOrderResponse[]> => {
      const response = await api.purchaseOrders.list({
        query: { ...orderQuery(filters), ...(q ? { q } : {}), pageSize: PICKER_PAGE_SIZE },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.orders;
    },
    placeholderData: keepPreviousData,
  });
}

export function usePurchaseOrdersHeld(ids: readonly string[]) {
  const key = [...new Set(ids)].sort();
  return useQuery({
    queryKey: [...PURCHASE_ORDERS_KEY, 'held', key],
    enabled: key.length > 0,
    queryFn: async (): Promise<PurchaseOrderResponse[]> => {
      const response = await api.purchaseOrders.list({
        query: { ids: pickerIds(key), pageSize: MAX_PICKER_IDS },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.orders;
    },
  });
}

/** The pair a reader recognises — the printed code and who it is with. */
export function purchaseOrderOption(order: PurchaseOrderResponse): TypeaheadOption {
  return { id: order.id, label: `${order.code} — ${order.partnerName}` };
}

export function usePurchaseOrderPicker(
  heldId: string | null | undefined,
  filters: OrderListFilters = {},
): PickerState<PurchaseOrderResponse> {
  const [query, setQuery] = React.useState('');
  const search = useDebounced(query);
  const ids = React.useMemo(() => (heldId ? [heldId] : []), [heldId]);
  const matches = usePurchaseOrderSearch(search, filters);
  const held = usePurchaseOrdersHeld(ids);
  return usePickerState<PurchaseOrderResponse>({
    query,
    setQuery,
    search,
    matches: matches.data,
    held: held.data,
    toOption: purchaseOrderOption,
    loading: matches.isFetching,
  });
}

export function usePurchaseOrder(orderId: string | undefined) {
  return useQuery({
    queryKey: [...PURCHASE_ORDERS_KEY, 'one', orderId],
    enabled: Boolean(orderId),
    queryFn: async (): Promise<PurchaseOrderResponse> => {
      const response = await api.purchaseOrders.get({ params: { orderId: orderId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePurchaseOrderRequest): Promise<PurchaseOrderResponse> => {
      const response = await api.purchaseOrders.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY }),
  });
}

export function useUpdatePurchaseOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdatePurchaseOrderRequest): Promise<PurchaseOrderResponse> => {
      const response = await api.purchaseOrders.update({ params: { orderId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY }),
  });
}

export function useDeletePurchaseOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await api.purchaseOrders.remove({ params: { orderId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY }),
  });
}

/** BR-PUR-01-01 — Draft → Approved by a purchasing.approve holder. */
export function useApprovePurchaseOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<PurchaseOrderResponse> => {
      const response = await api.purchaseOrders.approve({ params: { orderId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY }),
  });
}

/** The commitment door — Draft/Approved → Sent. */
export function useSendPurchaseOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // Wave 2: the answer also says whether the order's PDF went to the
    // supplier by mail (`emailedTo`), which the editor's banner reads.
    mutationFn: async (): Promise<SendPurchaseOrderResponse> => {
      const response = await api.purchaseOrders.send({ params: { orderId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY }),
  });
}

export function useCancelPurchaseOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    /** GEN-59: the cancel carries its typed reason. */
    mutationFn: async (reason: string): Promise<PurchaseOrderResponse> => {
      const response = await api.purchaseOrders.cancel({ params: { orderId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY }),
  });
}

/**
 * Wave 5E — AMEND a sent order: back to draft with a typed reason, so its
 * lines can be rewritten and it can be sent again as a revised copy.
 */
export function useReopenPurchaseOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string): Promise<PurchaseOrderResponse> => {
      const response = await api.purchaseOrders.reopen({ params: { orderId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY }),
  });
}

// ── Bills (PUR-03) ──────────────────────────────────────────────────────────

export interface BillListFilters {
  partnerId?: string | undefined;
  status?: BillResponse['status'] | undefined;
  purchaseOrderId?: string | undefined;
  /** GEN-50: the printed bill number or the supplier's name — the server narrows. */
  q?: string | undefined;
  /** Wave 3B — the list's window on the bill's invoice date, both inclusive. */
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

function billQuery(filters: BillListFilters) {
  return {
    ...(filters.partnerId ? { partnerId: filters.partnerId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.purchaseOrderId ? { purchaseOrderId: filters.purchaseOrderId } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
  };
}

/** PUR-03's register SCREEN — one page at a time (NFR-42). */
export function usePagedBills(filters: BillListFilters, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...BILLS_KEY, 'page', filters, page, pageSize],
    queryFn: async () => {
      const response = await api.purchaseInvoices.list({
        query: { ...billQuery(filters), page, pageSize },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/** The export's page read — the same query the screen shows, imperative so `fetchAllPages` can walk it. */
export async function fetchBillsPage(
  filters: BillListFilters,
  page: number,
  pageSize: number,
): Promise<{ rows: BillResponse[]; total: number }> {
  const response = await api.purchaseInvoices.list({ query: { ...billQuery(filters), page, pageSize } });
  if (response.status !== 200) throwApiError(response.status, response.body);
  return { rows: response.body.bills, total: response.body.total };
}

/** Wave 3B — PUR-03's list tiles over the whole register (PAGINATION.md §3 step 10). */
export function useBillStats() {
  return useQuery({
    queryKey: [...BILLS_KEY, 'stats'],
    queryFn: async () => {
      const response = await api.purchaseInvoices.stats();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/** GEN-50 — PUR-04's "which bill?" picker. */
export function useBillSearch(q: string, filters: BillListFilters = {}) {
  return useQuery({
    queryKey: [...BILLS_KEY, 'search', q, filters],
    queryFn: async (): Promise<BillResponse[]> => {
      const response = await api.purchaseInvoices.list({
        query: { ...billQuery(filters), ...(q ? { q } : {}), pageSize: PICKER_PAGE_SIZE },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.bills;
    },
    placeholderData: keepPreviousData,
  });
}

export function useBillsHeld(ids: readonly string[]) {
  const key = [...new Set(ids)].sort();
  return useQuery({
    queryKey: [...BILLS_KEY, 'held', key],
    enabled: key.length > 0,
    queryFn: async (): Promise<BillResponse[]> => {
      const response = await api.purchaseInvoices.list({
        query: { ids: pickerIds(key), pageSize: MAX_PICKER_IDS },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.bills;
    },
  });
}

export function billOption(bill: BillResponse): TypeaheadOption {
  return { id: bill.id, label: `${bill.code} — ${bill.partnerName}` };
}

export function useBillPicker(
  heldId: string | null | undefined,
  filters: BillListFilters = {},
): PickerState<BillResponse> {
  const [query, setQuery] = React.useState('');
  const search = useDebounced(query);
  const ids = React.useMemo(() => (heldId ? [heldId] : []), [heldId]);
  const matches = useBillSearch(search, filters);
  const held = useBillsHeld(ids);
  return usePickerState<BillResponse>({
    query,
    setQuery,
    search,
    matches: matches.data,
    held: held.data,
    toOption: billOption,
    loading: matches.isFetching,
  });
}

export function useBill(billId: string | undefined) {
  return useQuery({
    queryKey: [...BILLS_KEY, 'one', billId],
    enabled: Boolean(billId),
    queryFn: async (): Promise<BillResponse> => {
      const response = await api.purchaseInvoices.get({ params: { billId: billId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/**
 * PUR-03 §5e — the history card's data: the shared audit log filtered to THIS
 * bill, up to six entries newest first (the server owns the cap and the
 * order). Keyed under BILLS_KEY so every bill mutation above refreshes the
 * card in the same breath as the document it describes.
 */
export function useBillHistory(billId: string | undefined) {
  return useQuery({
    queryKey: [...BILLS_KEY, 'history', billId],
    enabled: Boolean(billId),
    queryFn: async (): Promise<BillHistoryResponse> => {
      const response = await api.purchaseInvoices.history({ params: { billId: billId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useCreateBill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBillRequest): Promise<BillResponse> => {
      const response = await api.purchaseInvoices.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: BILLS_KEY });
      void queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY });
    },
  });
}

export function useUpdateBill(billId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateBillRequest): Promise<BillResponse> => {
      const response = await api.purchaseInvoices.update({ params: { billId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BILLS_KEY }),
  });
}

export function useDeleteBill(billId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await api.purchaseInvoices.remove({ params: { billId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: BILLS_KEY });
      void queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY });
    },
  });
}

/** BR-PUR-03-16 — the audited variance override. */
export function useAcceptVariance(billId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string): Promise<BillResponse> => {
      const response = await api.purchaseInvoices.acceptVariance({
        params: { billId },
        body: { reason },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BILLS_KEY }),
  });
}

/** Draft → Posted; the order's billed figure moves, so orders refresh too. */
export function usePostBill(billId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    /**
     * Wave 1C: `allowUnreceived` is the approver's explicit say-so for a bill
     * on a sent order nothing has been received against (the server refuses
     * without it — `BILL_REFUSALS.nothingReceived`).
     */
    mutationFn: async (options: { allowUnreceived?: boolean } = {}): Promise<BillResponse> => {
      const response = await api.purchaseInvoices.post({
        params: { billId },
        body: options.allowUnreceived === true ? { allowUnreceived: true } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: BILLS_KEY });
      void queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY });
    },
  });
}

/** BR-PUR-03-11: cancel a POSTED bill, reason required (GEN-72). */
export function useCancelBill(billId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string): Promise<BillResponse> => {
      const response = await api.purchaseInvoices.cancel({ params: { billId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: BILLS_KEY });
      void queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY });
      void queryClient.invalidateQueries({ queryKey: GRN_KEY });
    },
  });
}

// ── Goods Received Notes (PUR-02) ───────────────────────────────────────

export const GRN_KEY = ['goods-receipts'] as const;

export interface GrnListFilters {
  purchaseOrderId?: string | undefined;
  status?: GoodsReceiptResponse['status'] | undefined;
  /** The search box: printed number, supplier, or field 9's supplier reference. */
  q?: string | undefined;
  /** Wave 3B — the list's window on the receipt date, both inclusive. */
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

function grnQuery(filters: GrnListFilters) {
  return {
    ...(filters.purchaseOrderId ? { purchaseOrderId: filters.purchaseOrderId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
  };
}

/** PUR-02's list, one page at a time (NFR-42). Both filters and the search run in SQL. */
export function useGoodsReceipts(
  filters: GrnListFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...GRN_KEY, filters, page, pageSize],
    queryFn: async () => {
      const response = await api.goodsReceipts.list({ query: { ...grnQuery(filters), page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/** The export's page read — the same query the screen shows, imperative so `fetchAllPages` can walk it. */
export async function fetchGoodsReceiptsPage(
  filters: GrnListFilters,
  page: number,
  pageSize: number,
): Promise<{ rows: GoodsReceiptResponse[]; total: number }> {
  const response = await api.goodsReceipts.list({ query: { ...grnQuery(filters), page, pageSize } });
  if (response.status !== 200) throwApiError(response.status, response.body);
  return { rows: response.body.receipts, total: response.body.total };
}

/** Wave 3B — PUR-02's list tiles over the whole register (PAGINATION.md §3 step 10). */
export function useGoodsReceiptStats() {
  return useQuery({
    queryKey: [...GRN_KEY, 'stats'],
    queryFn: async () => {
      const response = await api.goodsReceipts.stats();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useGoodsReceipt(receiptId: string | undefined) {
  return useQuery({
    queryKey: [...GRN_KEY, 'one', receiptId],
    enabled: Boolean(receiptId),
    queryFn: async (): Promise<GoodsReceiptResponse> => {
      const response = await api.goodsReceipts.get({ params: { receiptId: receiptId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/** Wave 5C — the receipt's history card: up to six audit rows, newest first. */
export function useGoodsReceiptHistory(receiptId: string | undefined) {
  return useQuery({
    queryKey: [...GRN_KEY, 'history', receiptId],
    enabled: Boolean(receiptId),
    queryFn: async (): Promise<GrnHistoryResponse> => {
      const response = await api.goodsReceipts.history({ params: { receiptId: receiptId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useCreateGoodsReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateGrnRequest): Promise<GoodsReceiptResponse> => {
      const response = await api.goodsReceipts.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: GRN_KEY }),
  });
}

export function useUpdateGoodsReceipt(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateGrnRequest): Promise<GoodsReceiptResponse> => {
      const response = await api.goodsReceipts.update({ params: { receiptId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: GRN_KEY }),
  });
}

export function useDeleteGoodsReceipt(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await api.goodsReceipts.remove({ params: { receiptId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: GRN_KEY }),
  });
}

/**
 * Post — add to stock (BR-PUR-02-01): the order's received figures, the
 * bill's match leg and the stock levels all move, so everything refreshes.
 */
export function usePostGoodsReceipt(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<GoodsReceiptResponse> => {
      const response = await api.goodsReceipts.post({ params: { receiptId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: GRN_KEY });
      void queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY });
      void queryClient.invalidateQueries({ queryKey: BILLS_KEY });
    },
  });
}

/** BR-PUR-02-08's tail: cancel a POSTED receipt, reason required (GEN-72). */
export function useCancelGoodsReceipt(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string): Promise<GoodsReceiptResponse> => {
      const response = await api.goodsReceipts.cancel({ params: { receiptId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: GRN_KEY });
      void queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY });
      void queryClient.invalidateQueries({ queryKey: BILLS_KEY });
    },
  });
}
